
import {Component, Vue} from "vue-property-decorator";
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import {mapState} from "vuex";
import DashboardRepository from "@/utils/API/DashboardRepository";
import ListRequest, {OrderType} from "@/models/ListRequest";
import {Recipe} from "@/models/Recipes/Recipe";
import {Dish} from "@/models/Dishes/Dish";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vTable from "@/components/vTable.vue";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import {
  debounce,
  getPreviewNutrientsForCountry,
  mapNutritions,
  tableMapper,
  parseISOString,
  millisToDays, round
} from "@/utils/Helpers";
import DishesRepository from "@/utils/API/DishesRepository";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";

/**
 * Chart.js instance
 * https://www.chartjs.org/docs/latest/
 */
const Chart = require("chart.js");
/**
 * numscroller instance
 */
const numscroller = require("@/assets/Customer/assets/js/numscroller-1.0.js");

/**
 * Model for storing totals state used by chart and counter widget
 */
interface Totals {
  totalRecipes: number;
  totalDishes: number;
  totalIngredients: number;
  chartData: any;
  [key: string]: number;
}

/**
 * Model for storing recipes state for the dashboard.
 */
interface RecentRecipes {
  listRequest: ListRequest;
  recipes: Recipe[];
  loading: boolean;
  searchString: string;
  hasRecipes: boolean;
}

/**
 * Model for storing dishes state for the dashboard.
 */
interface RecentDishes {
  listRequest: ListRequest;
  dishes: Dish[];
  loading: boolean;
  searchString: string;
  hasDishes: boolean;
}

@Component({
  components: {
    VUpsellModal,
    vTable,
  },
  methods: {
    millisToDays,
    parseISOString
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class Home extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  totals: Totals = {
    totalRecipes: 0,
    totalIngredients: 0,
    totalDishes: 0,
    chartData: 0,
  };
  recentRecipes: RecentRecipes = {
    listRequest: new ListRequest(
      {
        page: 1,
        pageSize: 5,
        orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
        filters: [{ name: "Name", value: "" }],
      },
      undefined,
      getPreviewNutrientsForCountry(NutritionLabelCountry.Canada)
    ),
    recipes: [],
    loading: true,
    searchString: "",
    hasRecipes: false,
  };
  recentDishes: RecentDishes = {
    listRequest: new ListRequest(
      {
        page: 1,
        pageSize: 5,
        orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
        filters: [{ name: "Name", value: "" }],
      },
      undefined,
      getPreviewNutrientsForCountry(NutritionLabelCountry.Canada)
    ),
    dishes: [],
    loading: true,
    searchString: "",
    hasDishes: false,
  };

  showTrialUpsellModal: boolean = false;

  editDish(id: number) {
    this.$router.push({ name: "dishesEdit", params: { id: id.toString() } });
  }

  editRecipe(id: number) {
    this.$router.push({ name: "recipesEdit", params: { id: id.toString() } });
  }

  /**
   * Debounced method for handling search recipes on user input.
   */
  searchRecipes() {
    if (this.recentRecipes.listRequest.queryOptions.filters) {
      this.recentRecipes.listRequest.queryOptions.filters.length = 0;
      this.recentRecipes.listRequest.queryOptions.filters.push({
        name: "Name",
        value: this.recentRecipes.searchString,
      });
    }
    debounce(
      () => {
        this.requestRecipes();
      },
      250,
      "request-recipes"
    );
  }

  // Show recipe cost link, check if user paid for recipe cost
  showRecipeCostLink() {
    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.RecipeCost);
    }
    return false;
  }

  handleRecipeCostButton() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
    }

    else {
      this.$router.push({ name: 'recipesCost' });
    }
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
  }

  /**
   * Calculates the number of days remaining in the user's free trial period.
   *
   * @return {number} The number of days remaining until the subscription end date, rounded to the nearest whole number. Returns 0 if the free trial has already ended.
   */
  getFreeTrialDaysRemaining(): number {
    return round(
      millisToDays(
        Math.abs(
          parseISOString(this.accountState.userDetails.subscriptionEndDate).getTime() - new Date().getTime()
        )
      ),
      0
    );
  }

  /**
   * Debounced method for handling search dishes on user input.
   */
  searchDishes() {
    if (this.recentDishes.listRequest.queryOptions.filters) {
      this.recentDishes.listRequest.queryOptions.filters.length = 0;
      this.recentDishes.listRequest.queryOptions.filters.push({
        name: "Name",
        value: this.recentDishes.searchString,
      });
    }
    debounce
      (() => {
         this.requestDishes();
       },
       250,
       "request-dishes"
      );
  }

  /**
   * Getter method to transform the list of recipes to grid data (vTable)
   */
  get transformRecipe() {
    return this.recentRecipes.recipes.map((x) => tableMapper(x, true));
  }

  /**
   * Getter method to transform the list of dishes to grid data (vTable)
   */
  get transformDish() {
    return this.recentDishes.dishes.map((x) => tableMapper(x, true));
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   * Used by both grids
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("ingr_table_name"),
        key: "Name",
      },
      {
        name: this.translations.get("ingr_table_ene"),
        key: "Energy",
      },
      {
        name: this.translations.get("ingr_calories_from_fat") + " (g)",
        key: "CaloriesFromFat",
      },
      {
        name: this.translations.get("ingr_table_fat") + " (g)",
        key: "Fat",
      },
      {
        name: this.translations.get("ingr_table_sat") + " (g)",
        key: "SaturatedFattyAcids",
      },
      {
        name: this.translations.get("ingr_table_trans") + " (g)",
        key: "TransFattyAcids",
      },
      {
        name: this.translations.get("ingr_table_chol") + " (mg)",
        key: "Cholesterol",
      },
      {
        name: this.translations.get("ingr_table_sod") + " (mg)",
        key: "Sodium",
      },
      {
        name: this.translations.get("ingr_table_carb") + " (g)",
        key: "Carbohydrate",
      },
      {
        name: this.translations.get("ingr_table_fib") + " (g)",
        key: "Fibre",
      },
      {
        name: this.translations.get("ingr_table_sug") + " (g)",
        key: "Sugars",
      },
      {
        name: this.translations.get("ingr_table_prot") + " (g)",
        key: "Protein",
      },
      {
        name: this.translations.get("ingr_table_vitD") + " (mcg)",
        key: "VitaminD",
      },
      {
        name: this.translations.get("ingr_table_vitc") + " (mg)",
        key: "VitaminC",
      },
      {
        name: this.translations.get("ingr_table_pot") + " (mg)",
        key: "Potassium",
      },
      {
        name: this.translations.get("ingr_table_calc") + " (mg)",
        key: "Calcium",
      },
      {
        name: this.translations.get("ingr_table_iron") + " (mg)",
        key: "Iron",
      },
      {
        name: "Total",
        key: "Weight",
      },
    ];
  }

  get translations() {
    return this.localizationState.translations;
  }

  /**
   * Method for handling recipes fetching and setting recipes state
   */
  requestRecipes() {
    this.recentRecipes.loading = true;
    RecipesRepository.listRecipes(this.recentRecipes.listRequest)
      .then((response) => {
        this.recentRecipes.recipes = response.data.result;
        this.recentRecipes.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.recentRecipes.loading = false;

        if (this.recentRecipes.recipes.length > 0) {
          this.recentRecipes.hasRecipes = true;
        }
        else {
          this.recentRecipes.hasRecipes = false;
        }
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  /**
   * Method for handling recipes fetching and setting dishes state
   */
  requestDishes() {
    this.recentDishes.loading = true;
    DishesRepository.listDishes(this.recentDishes.listRequest)
      .then((response) => {
        this.recentDishes.dishes = response.data.result;
        this.recentDishes.dishes.map((dish: any) => {
          dish.nutritionAnalysis = mapNutritions(dish.nutritionAnalysis);
        });
        this.recentDishes.loading = false;

        if (this.recentDishes.dishes.length > 0) {
          this.recentDishes.hasDishes = true;
        }
        else {
          this.recentDishes.hasDishes = false;
        }
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  /**
   * Method for initializing both Chart.js charts if there are recipes / dishes in the system
   * https://www.chartjs.org/docs/latest/
   */
  initCharts() {
    try {
      if (this.totals.totalRecipes > 0) {
        // @ts-ignore
        const recipesCtx = document.getElementById("recipesChart").getContext("2d");
        let recipesChart = new Chart(recipesCtx, {
          type: "pie",
          data: {
            labels: ["Low Cal.", "Moderate Cal.", "High Cal."],
            datasets: [
              {
                label: "# of Votes",
                data: [
                  this.totals.chartData.lowCaloriesRecipes,
                  this.totals.chartData.moderateCaloriesRecipes,
                  this.totals.chartData.highCaloriesRecipes,
                ],
                backgroundColor: ["rgb(124, 179, 66)", "#d3bb00", "#b71c1c"],
                borderColor: ["#efebe9", "#efebe9", "#efebe9"],
              },
            ],
          },
          options: {
            animation: {
              duration: 3000,
            },
          },
        });
      }

      if (this.totals.totalDishes > 0) {
        // @ts-ignore
        const dishesCtx = document.getElementById("dishesChart").getContext("2d");
        let dishesChart = new Chart(dishesCtx, {
          type: "pie",
          data: {
            labels: ["Low Cal.", "Moderate Cal.", "High Cal."],
            datasets: [
              {
                label: "# of Votes",
                data: [
                  this.totals.chartData.lowCaloriesDishes,
                  this.totals.chartData.moderateCaloriesDishes,
                  this.totals.chartData.highCaloriesDishes,
                ],
                backgroundColor: ["rgb(124, 179, 66)", "#d3bb00", "#b71c1c"],
                borderColor: ["#efebe9", "#efebe9", "#efebe9"],
              },
            ],
          },
          options: {
            animation: {
              duration: 2000,
            },
          },
        });
      }
    }
    catch (error) {
      console.log(error);
    }
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["home", "ingredients", "recipes", "dishes", "common", "dashboard"],
      })
      .then(() => {
        // Set corresponding title to the page (Home | MenuSano)
        document.title =
          this.translations.get("dashboard") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });

    // Fetch recipes
    this.requestRecipes();

    // Fetch dishes
    this.requestDishes();
  }

  // Fetch dashboard data in mounted() hook because we need to make sure the DOM is loaded
  // and ready to be modified by initCharts();
  mounted() {
    DashboardRepository.getDashboardData()
      .then((response) => {
        this.totals = response.data.result;
        this.initCharts();
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
}
export default Home;
